// ChoshiiLogo.tsx
import React from "react";

type Stop = { offset: string; color: string };

type Props = {
  text?: string;
  fontFamily?: string;
  letterSpacing?: number;
  shadow?: boolean;
  animateSheen?: boolean;
  goldStops?: Stop[];
  background?: string;
  padding?: number;                     // visual padding around the svg wrapper
  style?: React.CSSProperties;          // apply width here (px, %, clamp)
  onClick?: () => void;                 // click handler on the glyphs only
  clickTrimRatio?: number;
};

const DEFAULT_STOPS: Stop[] = [
  { offset: "0%",   color: "#6f5b2e" },
  { offset: "18%",  color: "#b08d3b" },
  { offset: "40%",  color: "#ffd87a" },
  { offset: "65%",  color: "#b88a2b" },
  { offset: "100%", color: "#695224" },
];

export default function ChoshiiLogo({
  text = "UNYDO",
  fontFamily = "serif",
  letterSpacing = 0.02,
  shadow = true,
  animateSheen = true,
  goldStops = DEFAULT_STOPS,
  background = "transparent",
  padding = 0,
  style,
  onClick,
  clickTrimRatio = 0.08, // trim 8% from the bottom by default
}: Props) {
  const id = React.useId();
  const gradId = `goldGrad-${id}`;
  const strokeGradId = `strokeGrad-${id}`;
  const sheenId = `sheen-${id}`;
  const maskId = `mask-${id}`;
  const clickableMaskId = `clickmask-${id}`;
  const filterId = `shadow-${id}`;
  const bevelId = `bevel-${id}`;
  const label = `${text} logo`;

  // Clamp trim ratio to sane bounds
  const trim = Math.max(0, Math.min(0.3, clickTrimRatio)); // 0–30%

  return (
    <div
      aria-label={label}
      role="img"
      style={{
        display: "inline-block",
        background,
        borderRadius: 16,
        padding,
        userSelect: "none",
        ...style, // set width here (e.g., { width: "80%" } or clamp)
      }}
    >
      <svg
        viewBox="0 0 1000 300"
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          pointerEvents: "none", // default: nothing inside gets events unless overridden
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Gold fill */}
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            {goldStops.map((s, i) => (
              <stop key={i} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>

          {/* Stroke gradient */}
          <linearGradient id={strokeGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f4e3a1" />
            <stop offset="50%" stopColor="#caa64f" />
            <stop offset="100%" stopColor="#8a6a2a" />
          </linearGradient>

          {/* Soft drop shadow */}
          <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000" floodOpacity="0.35" />
          </filter>

          {/* Subtle bevel */}
          <filter id={bevelId} x="-10%" y="-10%" width="120%" height="120%" filterUnits="objectBoundingBox">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" result="blur" />
            <feSpecularLighting in="blur" surfaceScale="2" specularConstant="0.55" specularExponent="18" lightingColor="#fff" result="spec">
              <fePointLight x="-50" y="-80" z="120" />
            </feSpecularLighting>
            <feComposite in="spec" in2="SourceAlpha" operator="in" result="specOut" />
            <feComposite in="SourceGraphic" in2="specOut" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" />
          </filter>

          {/* Animated sheen mask */}
          <linearGradient id={sheenId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#000" stopOpacity="0" />
            <stop offset="45%" stopColor="#fff" stopOpacity="0.9" />
            <stop offset="55%" stopColor="#fff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </linearGradient>
          <mask id={maskId} maskUnits="objectBoundingBox">
            <rect id={`sheen-rect-${id}`} x="-100%" y="0" width="300%" height="100%" fill={`url(#${sheenId})`} />
          </mask>

          {/* CLICKABLE MASK: trims the bottom of the clickable layer */}
          <clipPath id={clickableMaskId} clipPathUnits="objectBoundingBox">
            {/* x,y,width,height = 0..1 relative to the text bbox BEFORE clipping */}
            {/* Trim from bottom by reducing height */}
            <rect x="0" y="0" width="1" height={String(1 - trim)} />
          </clipPath>

          <style>{`
            @keyframes shimmer-${id} {
              0%   { transform: translateX(-66%); }
              100% { transform: translateX(0%); }
            }
            #sheen-rect-${id} {
              ${animateSheen ? `animation: shimmer-${id} 2.4s linear infinite;` : ""}
            }
            .logo-text-${id} { 
              font-family: ${fontFamily};
              font-weight: 600;
              letter-spacing: ${letterSpacing}em;
            }
          `}</style>
        </defs>

        <g transform="translate(500,150)">
          {/* CLICK TARGET: plain gold fill, no filters, with bottom trim */}
          <text
            className={`logo-text-${id}`}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="164"
            fill={`url(#${gradId})`}
            clipPath={`url(#${clickableMaskId})`}
            onClick={onClick}
            style={{
              pointerEvents: "visiblePainted", // only painted glyphs accept clicks
              cursor: onClick ? "pointer" : "inherit",
            }}
          >
            {text}
          </text>

          {/* Shadow (visual only) */}
          {shadow && (
            <text
              className={`logo-text-${id}`}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="164"
              fill="#000"
              opacity="0.55"
              filter={`url(#${filterId})`}
              style={{ pointerEvents: "none" }}
            >
              {text}
            </text>
          )}

          {/* Bevel (visual only) */}
          <text
            className={`logo-text-${id}`}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="164"
            fill={`url(#${gradId})`}
            filter={`url(#${bevelId})`}
            style={{ pointerEvents: "none" }}
          >
            {text}
          </text>

          {/* Stroke (visual only) */}
          <text
            className={`logo-text-${id}`}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="164"
            fill="none"
            stroke={`url(#${strokeGradId})`}
            strokeWidth="1.4"
            opacity="0.9"
            style={{ pointerEvents: "none" }}
          >
            {text}
          </text>

          {/* Sheen (visual only) */}
          {animateSheen && (
            <g mask={`url(#${maskId})`} opacity="0.6" style={{ pointerEvents: "none" }}>
              <text
                className={`logo-text-${id}`}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="164"
                fill="#ffffff"
              >
                {text}
              </text>
            </g>
          )}
        </g>
      </svg>
    </div>
  );
}
